import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getHeaders } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface Connection {
  id?: string;
  connectionId?: string;
  name?: string;
  [key: string]: any;
}

interface Props {
  propertyId: string | undefined;
  currentIntegration?: any | null;
}

const AccountingIntegrationCard: React.FC<Props> = ({ propertyId, currentIntegration }) => {
  const { toast } = useToast();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(false);
  const [setting, setSetting] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // derive existing mapping if present on integration
  const existingExternalId = (
    currentIntegration && (
      currentIntegration.externalPropertyId ||
      currentIntegration.external_property_id ||
      currentIntegration.externalId ||
      currentIntegration.accountingExternalId ||
      currentIntegration.settings?.externalPropertyId
    )
  )?.toString();

  const existingExternalName = (
    currentIntegration && (
      currentIntegration.externalPropertyName ||
      currentIntegration.external_property_name ||
      currentIntegration.accountingExternalName ||
      currentIntegration.settings?.externalPropertyName
    )
  ) || "";

  // exact fields (display only when explicitly present)
  const exactExternalId = currentIntegration?.externalId ? String(currentIntegration.externalId) : null;
  const exactExternalName = currentIntegration?.externalPropertyName || null;

  useEffect(() => {
    if (existingExternalId) setSelectedConnectionId(existingExternalId);
  }, [existingExternalId]);

  const fetchConnections = async () => {
    if (!currentIntegration) {
      toast({ title: "No accounting integration", description: "Connect an accounting integration before fetching connections.", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      const integrationName = currentIntegration.name || currentIntegration.provider || "Unknown";

      const body = JSON.stringify({ integrationId, propertyId, integrationName});

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/payroll/connections`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) throw new Error(`Failed to fetch connections: ${resp.status}`);

      const data = await resp.json();
      let list: Connection[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && data.connections) list = data.connections;
      else if (data && data.data) list = data.data;
      else if (data && data.body) list = data.body;

      setConnections(list || []);
      toast({ title: "Connections loaded" });
    } catch (error) {
      console.error("Error fetching connections", error);
      toast({ title: "Error fetching connections", description: String(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const updateIntegration = async () => {
    if (!selectedConnectionId) {
      toast({ title: "Select a connection", description: "Choose a connection before updating integration", variant: "destructive" });
      return;
    }
    if (!currentIntegration) {
      toast({ title: "No accounting integration", description: "Connect an accounting integration before updating.", variant: "destructive" });
      return;
    }

    try {
      setSetting(true);
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      // const providerName = (currentIntegration.name || currentIntegration.provider || "").toString();

      const selected = connections.find(c => (c.externalId || '').toString() === selectedConnectionId);
      const externalName = selected ? (selected.externalPropertyName) : '';

      const bodyPayload = {
        propertyId,
        integrationId,
        externalPropertyId: selectedConnectionId,
        externalPropertyName: externalName
      };

      const body = JSON.stringify(bodyPayload);
      console.debug("Updating integration with connection:", bodyPayload);

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/integrations/updatePropertyIntegration`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        const errBody = await resp.text();
        throw new Error(errBody || `Status ${resp.status}`);
      }

      toast({ title: "Integration updated", description: "External connection mapped to this property" });
    } catch (error) {
      console.error("Error updating integration", error);
      toast({ title: "Error updating integration", description: String(error), variant: "destructive" });
    } finally {
      setSetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accounting Connections</CardTitle>
        <CardDescription>Retrieve Accounting connections and map to this property</CardDescription>
      </CardHeader>

      <CardContent>
        {(exactExternalId || exactExternalName) && (
          <div className="mb-3 rounded-md bg-muted p-3">
            <div className="text-sm font-medium">Mapped External Connection</div>
            <div className="text-sm">{exactExternalName || "—"}</div>
            <div className="text-sm text-muted-foreground">External ID: {exactExternalId || "—"}</div>
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex space-x-2">
            <Button size="sm" onClick={fetchConnections} disabled={loading}>{loading ? 'Loading...' : 'Fetch Connections'}</Button>
            <Button size="sm" onClick={updateIntegration} disabled={setting || !selectedConnectionId}>{setting ? 'Setting...' : 'Set Connection'}</Button>
          </div>
        </div>

        {connections.length === 0 ? (
          <p className="text-sm text-muted-foreground">No connections loaded.</p>
        ) : (
          <div className="space-y-2">
            <RadioGroup value={selectedConnectionId || undefined} onValueChange={(v) => setSelectedConnectionId(v)}>
              {connections.map((c, idx) => {
                const id = c.externalId.toString();
                const name = c.externalPropertyName;
                return (
                  <div key={id} className="flex items-center justify-between p-2 rounded hover:bg-muted">
                    <div>
                      <div className="font-medium">{name}</div>
                      <div className="text-sm text-muted-foreground">ID: {id}</div>
                    </div>
                    <RadioGroupItem value={id} />
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountingIntegrationCard;
